/**
 * The small shared pieces. Nothing here knows about the Act; they are the
 * panel, the notice, the chip row and the three states every screen has to be
 * able to show — loading, empty, and something went wrong.
 */

import type { ReactNode } from "react";
import { bi, type Bilingual, type Provenance } from "@ksidc/ontology";
import { ProvenanceChip, useLang } from "@ksidc/client";

export function Panel({
  title,
  provenance,
  children,
  className,
}: {
  title?: Bilingual;
  provenance?: Provenance | null;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const { lang, t } = useLang();
  return (
    <section className={["panel", className].filter(Boolean).join(" ")}>
      {title ? <h2>{t(title)}</h2> : null}
      {provenance ? (
        <div className="prov-row">
          <ProvenanceChip provenance={provenance} lang={lang} />
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Callout({
  tone = "plain",
  title,
  provenance,
  children,
}: {
  tone?: "plain" | "warn" | "alarm" | "calm";
  title?: Bilingual;
  provenance?: Provenance | null;
  children?: ReactNode;
}): JSX.Element {
  const { lang, t } = useLang();
  const toneClass =
    tone === "warn"
      ? "callout-warn"
      : tone === "alarm"
        ? "callout-alarm"
        : tone === "calm"
          ? "callout-calm"
          : "";
  return (
    <div className={["callout", toneClass].filter(Boolean).join(" ")} role="note">
      {title ? <p className="callout-title">{t(title)}</p> : null}
      {children}
      {provenance ? (
        <div className="prov-row">
          <ProvenanceChip provenance={provenance} lang={lang} />
        </div>
      ) : null}
    </div>
  );
}

/** A row of provenance chips, for a statement resting on more than one source. */
export function ProvenanceRow({
  items,
}: {
  items: (Provenance | null | undefined)[];
}): JSX.Element | null {
  const { lang } = useLang();
  const present = items.filter((p): p is Provenance => Boolean(p));
  if (present.length === 0) return null;
  return (
    <div className="prov-row">
      {present.map((p, i) => (
        <ProvenanceChip key={`${p.kind}-${p.ref}-${i}`} provenance={p} lang={lang} />
      ))}
    </div>
  );
}

export function Eyebrow({ text }: { text: Bilingual }): JSX.Element {
  const { t } = useLang();
  return <p className="eyebrow">{t(text)}</p>;
}

export function Loading({ what }: { what?: Bilingual }): JSX.Element {
  const { t } = useLang();
  const label = what ?? bi("Loading", "വായിച്ചെടുക്കുന്നു");
  return (
    <p className="muted" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" /> {t(label)}…
    </p>
  );
}

export function ErrorNote({ error }: { error: unknown }): JSX.Element {
  const { t } = useLang();
  const message = error instanceof Error ? error.message : String(error);
  return (
    <Callout
      tone="alarm"
      title={bi("That did not work", "അത് പ്രവർത്തിച്ചില്ല")}
    >
      <p>
        {t(
          bi(
            "The system could not complete what you asked. Nothing has been lost. Try again, and if it keeps happening tell the board office what you were doing.",
            "നിങ്ങൾ ആവശ്യപ്പെട്ടത് പൂർത്തിയാക്കാൻ സംവിധാനത്തിന് കഴിഞ്ഞില്ല. ഒന്നും നഷ്ടപ്പെട്ടിട്ടില്ല. വീണ്ടും ശ്രമിക്കുക; ആവർത്തിക്കുകയാണെങ്കിൽ നിങ്ങൾ എന്താണ് ചെയ്തിരുന്നത് എന്ന് ബോർഡ് ഓഫീസിനെ അറിയിക്കുക.",
          ),
        )}
      </p>
      <p className="muted">{message}</p>
    </Callout>
  );
}

export function EmptyState({
  title,
  body,
  provenance,
}: {
  title: Bilingual;
  body: Bilingual;
  provenance?: Provenance | null;
}): JSX.Element {
  const { t } = useLang();
  return (
    <div className="callout">
      <p className="callout-title">{t(title)}</p>
      <p className="muted" style={{ marginBottom: 0 }}>
        {t(body)}
      </p>
      <ProvenanceRow items={[provenance]} />
    </div>
  );
}

export function Fact({
  label,
  value,
  emphasis,
  provenance,
}: {
  label: Bilingual;
  value: string;
  emphasis?: boolean;
  provenance?: Provenance | null;
}): JSX.Element {
  const { t } = useLang();
  return (
    <div className={["fact", emphasis ? "fact-deemed" : ""].filter(Boolean).join(" ")}>
      <span className="fact-label">
        {t(label)}
        <ProvenanceRow items={[provenance]} />
      </span>
      <span className="fact-value">{value}</span>
    </div>
  );
}

export function CheckMark(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
      <path
        d="M2.5 8.5l3.5 3.5 7.5-8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Badge({
  text,
  tone = "plain",
}: {
  text: Bilingual;
  tone?: "plain" | "warn" | "alarm" | "calm";
}): JSX.Element {
  const { t } = useLang();
  const toneClass =
    tone === "warn"
      ? "badge-warn"
      : tone === "alarm"
        ? "badge-alarm"
        : tone === "calm"
          ? "badge-calm"
          : "";
  return <span className={["badge", toneClass].filter(Boolean).join(" ")}>{t(text)}</span>;
}
